import { PRESET_OPTIONS } from 'consts/dashboard';
import type { Page } from 'consts/pages';

import { CONFIG_VERSION, type Widget } from './config';

// Migrations operate on persisted configs of evolving (and partly legacy) shape, so the
// column/widget arrays are modelled loosely as `string | Widget` entries.
type ConfigArray = Array<string | Widget>;

export type MigratableConfig = {
  version?: number;
  presets?: Record<string, Record<string, ConfigArray>>;
  columnsConfig?: Record<string, ConfigArray | Record<string, ConfigArray>>;
  [key: string]: unknown;
};

const swapRiskScore = (c: string | Widget): string | Widget => (c === 'risk_score' ? 'deviation_score' : c);

export const MIGRATIONS: Record<number, (config: MigratableConfig) => MigratableConfig> = {
  [1]: (config) => {
    if (config.overview) {
      config.events = config.overview;
      delete config.overview;
    }

    config.presets = {};

    for (const page in PRESET_OPTIONS) {
      const oldConfig = config[page] as ConfigArray | undefined;
      config.presets[page] = (PRESET_OPTIONS[page as Page] ?? []).reduce<Record<string, ConfigArray>>((acc, preset) => {
        const presetConfig = preset.value === 'custom' && oldConfig?.length ? oldConfig : preset.defaultWidgets;

        return {
          ...acc,
          [preset.value]: presetConfig,
        };
      }, {});

      delete config[page];
    }

    delete config.loadedConfig;
    delete config.currentDashboard;
    delete config.cloudapi;
    delete config.identityManagement;
    delete config.multistream;

    config.version = 1;

    return config;
  },
  [2]: (config) => {
    const { log, ...rest } = config.columnsConfig || {};

    for (const page of Object.keys(rest)) {
      config.columnsConfig![page] = (config.columnsConfig![page] as ConfigArray).map(swapRiskScore);
    }

    const logColumns = (log as Record<string, ConfigArray>) || {};
    const remappedLog: Record<string, ConfigArray> = {};
    for (const logType of Object.keys(logColumns)) {
      remappedLog[logType] = logColumns[logType].map(swapRiskScore);
    }
    if (config.columnsConfig) {
      config.columnsConfig.log = remappedLog;
    }

    for (const pages of Object.keys(config.presets!)) {
      for (const preset of Object.keys(config.presets![pages])) {
        config.presets![pages][preset] = config.presets![pages][preset].map(swapRiskScore);
      }
    }

    config.version = 2;

    return config;
  },
};

if (import.meta.env.DEV && !MIGRATIONS[CONFIG_VERSION]) {
  alert(`No migration found for config version ${CONFIG_VERSION}`);
}

export const migrate = (config: MigratableConfig): MigratableConfig => {
  if (!config.version) {
    config.version = 0;
  }

  if (config.version === CONFIG_VERSION) {
    return config;
  }

  for (let i = (config.version ?? 0) + 1; i <= CONFIG_VERSION; i++) {
    config = MIGRATIONS[i](config);
  }

  return config;
};
